<template>
  <ul class="options-list-container">
    <span v-if="title" class="options-list-title">{{ $t(title) }}</span>
    <template v-for="(option, key) in optionsObject.options">
      <span v-if="!!option.optGroupLabel" :key="`label-${key}`" class="options-group-label">
        {{ $t(option.optGroupLabel) }}
      </span>
      <li v-else :key="`item-${key}`">
        <!-- Link type option -->
        <a
          v-if="!!option.optHref"
          class="options-list-item -t_medium compact-text"
          :class="{
            disabled: option.disabled,
            '--grouped': optionsObject.grouped && !unpadGrouped,
          }"
          @click.stop="handleLinkClick"
          :href="option.optHref"
          target="_blank"
        >
          <sword-icon-wuk class="item-icon -mx_1" :name="option.optIcon" />
          <span class="item-text">
            {{ $t(option.optLabel) }}
          </span>
          <sword-icon-wuk class="hyperlink-icon -ml_1" name="external-link" />
        </a>
        <!-- Phone number type option -->
        <dial-code-item
          v-if="option.optDialCode"
          :country-code="option.optValue"
          :label="option.optLabel"
          :selected="!!option.optValue && value === option.optValue"
          actionable
          mobile
          @click.native="selectOption(option)"
        >
        </dial-code-item>
        <!-- Regular type option -->
        <div
          v-else
          class="options-list-item compact-text"
          :class="[itemClass(option), `--align-${itemAlign}`]"
          @click.prevent="selectOption(option)"
        >
          <sword-icon-wuk
            v-if="optionsHaveIcon && (!option.iconPosition || option.iconPosition === 'right')"
            class="item-icon"
            :name="option.optIcon"
          />
          <span class="item-text">
            <span class="item-label">
              {{ $t(option.optLabel) }}
            </span>
            <span v-if="showCounters && option.optCounter != undefined" class="item-counter">
              ({{ option.optCounter }})
            </span>
          </span>
          <sword-icon-wuk
            v-if="option.copyable || (optionsHaveIcon && option.iconPosition === 'left')"
            class="item-icon"
            :name="option.copyable ? 'copy' : option.optIcon"
          />
        </div>
      </li>
    </template>
  </ul>
</template>

<script>
import SwordIconWuk from '@ui-kit/components/icons/SwordIcon.vue';
import computeOptions from '@ui-kit/scripts/utils/options-mapper';
import DialCodeItem from '@ui-kit/components/micro/dialCodeItem.vue';

export default {
  name: 'SwordListWuk',
  components: {
    SwordIconWuk,
    DialCodeItem,
  },
  props: {
    title: {
      type: String,
    },
    value: {
      type: [String, Number],
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    unpadGrouped: {
      type: Boolean,
      default: false,
    },
    showCounters: {
      type: Boolean,
      default: false,
    },
    itemAlign: {
      type: String,
      validator: value => ['center', 'start', 'end'].includes(value),
    },
  },
  computed: {
    optionsHaveIcon() {
      return this.optionsObject.options.some(op => op.optIcon);
    },
    optionsObject() {
      return computeOptions(this.options);
    },
    itemAlignClass() {
      return `--align-${this.itemAlign}`;
    },
  },
  methods: {
    itemClass(option) {
      return {
        selected: !!option.optValue && this.value === option.optValue,
        disabled: option.disabled,
        '--grouped': this.optionsObject.grouped && !this.unpadGrouped,
      };
    },
    selectOption(option) {
      this.$emit('click', option);
    },
    handleLinkClick() {
      this.$emit('link-click');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@ui-kit/styles/inputs/shared/_options-lists.scss";

@mixin option-item {
  height: 3rem;
  font-size: 0.875rem;
  padding: 0.75rem 1rem;
  @extend %t3;
}

@mixin option-item-mobile {
  height: 3rem;
  font-size: 1rem;
  padding: $option-list-mobile-item-padding;
}

.options-list-container {
  background-color: white;
  overflow: auto;
  display: flex;
  flex-direction: column;

  .options-list-title {
    display: flex;
    align-items: center;
    height: 3.25rem;
    color: $option-list-title-color;
    padding: 0 1rem;
    @extend %t1;
    @extend %-t_semi-bold;
  }

  .options-group-label {
    display: flex;
    align-items: flex-end;
    height: 3rem;
    color: $option-list-label-color;
    padding: 0 1rem;
    @extend %t4;
    @extend %-t_semi-bold;
  }

  .options-list-item {
    @include option-item;
    @include options-list-item-align();

    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $option-list-item-color;
    cursor: pointer;
    gap: 0.5rem;

    @media screen and (max-width: $max-smartphone-size) {
      @include option-item-mobile;
    }

    &.disabled {
      background-color: $option-list-disabled-item-bg-color;
      color: $option-list-disabled-item-color;
      cursor: default;
      pointer-events: none;
    }

    &.selected {
      color: $option-list-selected-item-color;
      background-color: $option-list-selected-item-background-color;
    }

    &.selected {
      @extend %-t_medium;
    }

    .item-icon {
      flex: none;
      width: 1.25rem;
      height: 1.25rem;
    }

    &:hover {
      background-color: $secondary-color-light;
    }

    .item-text {
      display: flex;
      min-width: 1px;
      flex-grow: 1;
      font-size: $options-list-items-text-font-size;
      font-weight: $options-list-items-text-font-weight;

      .item-label {
        @extend %compact-text;
      }
    }

    .item-counter {
      display: flex;

      &:before {
        content: " ";
        white-space: pre;
      }
    }

    .hyperlink-icon {
      flex: none;
      width: 1.25rem;
      height: 1.25rem;
      fill: $option-list-hyperlink-icon-color;
    }

    &.--align-center,
    &.--align-start,
    &.--align-end {
      .item-text {
        flex-grow: 0;
      }
    }
  }

  @media screen and (max-width: $max-smartphone-size) {
    .options-list-item,
    .options-group-label {
      @include option-item-mobile;
    }
  }
}
</style>
